import React, { useState, useEffect } from 'react';
import { X, Trash2, Search, Calendar, BarChart3, Package, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/apiService';

const ManageSuggestionsModal = ({ onClose }) => {
  const { t } = useLanguage();
  const [learnedItems, setLearnedItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [sortBy, setSortBy] = useState('frequency'); // frequency, lastUsed, name
  const [filterBy, setFilterBy] = useState('all'); // all, unused, recent

  useEffect(() => {
    loadLearnedItems();
  }, []);

  useEffect(() => {
    filterAndSortItems();
  }, [learnedItems, searchTerm, sortBy, filterBy]);

  const loadLearnedItems = async () => {
    try {
      setLoading(true);
      const items = await apiService.getLearnedItems();
      setLearnedItems(items);
    } catch (error) {
      console.error('Error loading learned items:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...learnedItems];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (filterBy === 'unused') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(item => new Date(item.lastUsed) < thirtyDaysAgo);
    } else if (filterBy === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(item => new Date(item.lastUsed) > sevenDaysAgo);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'frequency':
          return b.frequency - a.frequency;
        case 'lastUsed':
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;

    try {
      const itemsToDelete = Array.from(selectedItems);
      await apiService.deleteMultipleLearnedItems(itemsToDelete);
      
      // Remove deleted items from state
      setLearnedItems(prev => prev.filter(item => !selectedItems.has(item.originalName)));
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  };

  const handleDeleteSingle = async (itemName) => {
    try {
      await apiService.deleteLearnedItem(itemName);
      setLearnedItems(prev => prev.filter(item => item.originalName !== itemName));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const toggleItemSelection = (originalName) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(originalName)) {
      newSelected.delete(originalName);
    } else {
      newSelected.add(originalName);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.originalName)));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getFrequencyLevel = (frequency) => {
    if (frequency >= 10) return 'high';
    if (frequency >= 5) return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal manage-suggestions-modal">
          <div className="loading">Loading suggestions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal manage-suggestions-modal">
        <div className="modal-header">
          <h3>{t('manageSuggestions', 'Manage Suggestions')}</h3>
          <button onClick={onClose} className="modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Controls */}
          <div className="suggestions-controls">
            <div className="search-section">
              <div className="search-input-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder={t('searchSuggestions', 'Search suggestions...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            <div className="filter-sort-section">
              <div className="filter-group">
                <label>{t('show', 'Show')}:</label>
                <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
                  <option value="all">{t('allItems', 'All items')} ({learnedItems.length})</option>
                  <option value="unused">{t('unused', 'Unused (30+ days)')}</option>
                  <option value="recent">{t('recent', 'Recent (7 days)')}</option>
                </select>
              </div>

              <div className="sort-group">
                <label>{t('sortBy', 'Sort by')}:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="frequency">{t('frequency', 'Frequency')}</option>
                  <option value="lastUsed">{t('lastUsed', 'Last used')}</option>
                  <option value="name">{t('name', 'Name')}</option>
                </select>
              </div>
            </div>

            {filteredItems.length > 0 && (
              <div className="bulk-actions">
                <button 
                  onClick={toggleSelectAll}
                  className="select-all-btn"
                >
                  <Check size={16} />
                  {selectedItems.size === filteredItems.length ? 
                    t('deselectAll', 'Deselect all') : 
                    t('selectAll', 'Select all')
                  }
                </button>
                
                {selectedItems.size > 0 && (
                  <button 
                    onClick={handleDeleteSelected}
                    className="delete-selected-btn"
                  >
                    <Trash2 size={16} />
                    {t('deleteSelected', 'Delete selected')} ({selectedItems.size})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Items List */}
          <div className="suggestions-list">
            {filteredItems.length === 0 ? (
              <div className="empty-state">
                <Package size={48} />
                <h4>{t('noSuggestions', 'No suggestions found')}</h4>
                <p>{t('noSuggestionsDesc', 'Try adjusting your search or filters.')}</p>
              </div>
            ) : (
              filteredItems.map(item => (
                <div 
                  key={item.originalName} 
                  className={`suggestion-item ${selectedItems.has(item.originalName) ? 'selected' : ''}`}
                >
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.originalName)}
                      onChange={() => toggleItemSelection(item.originalName)}
                    />
                  </div>

                  <div className="item-info">
                    <div className="item-name">{item.name}</div>
                    <div className="item-meta">
                      <span className="category">{item.category}</span>
                      <span className={`frequency frequency-${getFrequencyLevel(item.frequency)}`}>
                        <BarChart3 size={12} />
                        {item.frequency}x
                      </span>
                      <span className="last-used">
                        <Calendar size={12} />
                        {formatDate(item.lastUsed)}
                      </span>
                    </div>
                  </div>

                  <button 
                    onClick={() => handleDeleteSingle(item.originalName)}
                    className="delete-item-btn"
                    title={t('deleteItem', 'Delete this suggestion')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="stats">
            {t('totalSuggestions', 'Total suggestions')}: {learnedItems.length} | 
            {t('showing', 'Showing')}: {filteredItems.length}
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            {t('close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageSuggestionsModal;
